import { useMemo, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router";
import { FileText, AlertTriangle, Plus, Pencil, ToggleLeft, ToggleRight, Trash2, Search } from "lucide-react";
import { Button, EmptyState } from "../../components/ui";
import { AgreementCard, type Agreement } from "./AgreementCard";
import { DefaultsStrip } from "./DefaultsStrip";
import {
  AgreementsToolbar,
  type ToolbarFilters,
} from "./AgreementsToolbar";
import { CoverageMatrix } from "./CoverageMatrix";
import { computeCoverage } from "./coverage";
import { ALL_BOOKING_TYPES } from "./catalog/queries";
import { STATE_OPTIONS, type BookingTypeRecord } from "./catalog/types";
import {
  ALL_AGREEMENT_TYPES,
  CREATE_AGREEMENT_TYPE,
  UPDATE_AGREEMENT_TYPE,
  DELETE_AGREEMENT_TYPE,
  type AgreementTypeRecord,
} from "../../apollo/agreementTypes";
import { useToast } from "../../components/ui/Toast";
import { slugify } from "../../utils/slugify";

const ALL_AGREEMENTS = gql`
  query AllAgreementsForListing {
    allAgreements(page: 0, perPage: 1000, sortField: "createdAt", sortOrder: "DESC") {
      id
      name
      agreementType
      agreementTypeId
      state
      jurisdictionKind
      jurisdictionValue
      isActive
      headerHtml
      footerHtml
      countersignerName
      countersignerSignatureKey
      marginTop
      marginBottom
      marginLeft
      marginRight
      topLeftHtml
      topRightHtml
      showFirstPageMargins
      signatureBlockHtml
      previewImageUrl
      previewGeneratedAt
      createdAt
    }
  }
`;

const TENANT_DEFAULTS = gql`
  query TenantSettingsForListing {
    tenantSettings {
      id
      name
      defaultHeaderHtml
      defaultFooterHtml
      defaultCountersignerName
      defaultMarginTop
      defaultMarginBottom
      defaultMarginLeft
      defaultMarginRight
      defaultPreviewImageUrl
      defaultPreviewGeneratedAt
    }
  }
`;

interface AllAgreementsData {
  allAgreements: Agreement[];
}

interface AllBookingTypesData {
  allBookingTypes: BookingTypeRecord[];
}

interface TenantDefaultsData {
  tenantSettings: {
    id: string;
    name: string;
    defaultHeaderHtml: string | null;
    defaultFooterHtml: string | null;
    defaultCountersignerName: string | null;
    defaultMarginTop: number | null;
    defaultMarginBottom: number | null;
    defaultMarginLeft: number | null;
    defaultMarginRight: number | null;
    defaultPreviewImageUrl: string | null;
    defaultPreviewGeneratedAt: string | null;
  };
}

type ActiveTab = "agreements" | "agreement-types" | "type-mappings";

const INITIAL_FILTERS: ToolbarFilters = {
  search: "",
  type: "",
  state: "",
  status: "all",
};

function CardSkeleton() {
  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden flex flex-col">
      <div className="bg-canvas/50 p-5 flex items-center justify-center">
        <div className="w-[110px] aspect-[8.5/11] rounded-sm bg-canvas-2 pulse-soft"></div>
      </div>
      <div className="px-4 py-3 hair-t border-t flex-1 space-y-2">
        <div className="h-3 bg-canvas-2 rounded w-3/4 pulse-soft"></div>
        <div className="h-2 bg-canvas-2 rounded w-1/2 pulse-soft"></div>
      </div>
    </div>
  );
}

/* ── Jurisdiction identity key for the false-warning fix ─────────── */

/** Build a string that uniquely identifies a jurisdiction slot. */
function jurisdictionIdentity(a: Agreement): string {
  const kind = a.jurisdictionKind ?? "national";
  if (kind === "national") return "national";
  const state = a.state ?? "";
  if (kind === "state") return `state:${state}`;
  const value = a.jurisdictionValue ?? "";
  return `${kind}:${state}:${value}`;
}

/**
 * Count jurisdiction-identity collisions among active versions.
 * Returns the number of EXTRA active versions sharing a jurisdiction
 * (i.e. count - 1 for each duplicated identity). Two active versions
 * in different jurisdictions (WI + CA) is healthy and returns 0.
 */
function countJurisdictionCollisions(versions: Agreement[]): number {
  const active = versions.filter((v) => v.isActive);
  if (active.length <= 1) return 0;
  const seen = new Map<string, number>();
  for (const a of active) {
    const key = jurisdictionIdentity(a);
    seen.set(key, (seen.get(key) ?? 0) + 1);
  }
  let collisions = 0;
  for (const count of seen.values()) {
    if (count > 1) collisions += count - 1;
  }
  return collisions;
}

/* ── Agreement Types management tab ──────────────────────────────── */

type ATStatusFilter = "all" | "active" | "retired";

function AgreementTypesTab({
  agreementTypes,
  agreementTypesLoading,
  agreements,
  services,
}: {
  agreementTypes: AgreementTypeRecord[];
  agreementTypesLoading: boolean;
  agreements: Agreement[];
  services: BookingTypeRecord[];
}) {
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState<ATStatusFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Create form state
  const [newName, setNewName] = useState("");
  const [newTypeKey, setNewTypeKey] = useState("");
  const [newTypeKeyEdited, setNewTypeKeyEdited] = useState(false);
  const [newDescription, setNewDescription] = useState("");
  const [newSortOrder, setNewSortOrder] = useState<number | "">("");

  // Edit form state
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editSortOrder, setEditSortOrder] = useState<number | "">("");
  const [editIsActive, setEditIsActive] = useState(true);

  const [createAgreementType, { loading: creating }] = useMutation(CREATE_AGREEMENT_TYPE);
  const [updateAgreementType, { loading: updating }] = useMutation(UPDATE_AGREEMENT_TYPE);
  const [deleteAgreementType, { loading: deleting }] = useMutation(DELETE_AGREEMENT_TYPE);

  // Build a set of type IDs that have agreements or services pointing at them
  const usedTypeIds = useMemo(() => {
    const ids = new Set<string>();
    for (const a of agreements) {
      if (a.agreementTypeId) ids.add(a.agreementTypeId);
    }
    for (const s of services) {
      if (s.agreementTypeId) ids.add(s.agreementTypeId);
    }
    return ids;
  }, [agreements, services]);

  // Also track references via the legacy `agreementType` string, so a type is
  // considered in-use even when a referrer only carries the string (null id) —
  // otherwise the Delete button could orphan a type that's still resolving via
  // the dual-accept string fallback.
  const usedTypeKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const a of agreements) {
      if (a.agreementType) keys.add(a.agreementType);
    }
    for (const s of services) {
      if (s.agreementType) keys.add(s.agreementType);
    }
    return keys;
  }, [agreements, services]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return agreementTypes.filter((t) => {
      if (statusFilter === "active" && !t.isActive) return false;
      if (statusFilter === "retired" && t.isActive) return false;
      if (q && !t.name.toLowerCase().includes(q) && !t.typeKey.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [agreementTypes, statusFilter, searchQuery]);

  function resetCreateForm() {
    setNewName("");
    setNewTypeKey("");
    setNewTypeKeyEdited(false);
    setNewDescription("");
    setNewSortOrder("");
    setShowCreateForm(false);
  }

  function startEditing(t: AgreementTypeRecord) {
    setEditingId(t.id);
    setEditName(t.name);
    setEditDescription(t.description ?? "");
    setEditSortOrder(t.sortOrder ?? "");
    setEditIsActive(t.isActive);
  }

  function cancelEditing() {
    setEditingId(null);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const effectiveKey = newTypeKey.trim() || slugify(newName);
    if (!newName.trim() || !effectiveKey) return;
    // Friendly pre-check for the UNIQUE(tenant_id, type_key) constraint — the
    // management tab loads all types (incl. retired), so we can catch a slug
    // collision here instead of surfacing a raw MySQL IntegrityError.
    if (
      agreementTypes.some(
        (t) => t.typeKey.toLowerCase() === effectiveKey.toLowerCase(),
      )
    ) {
      toast.push({
        kind: "error",
        title: "Key already in use",
        description: `An agreement type with key "${effectiveKey}" already exists (it may be retired). Pick a different name or key.`,
      });
      return;
    }
    try {
      await createAgreementType({
        variables: {
          name: newName.trim(),
          typeKey: effectiveKey,
          description: newDescription.trim() || null,
          sortOrder: newSortOrder === "" ? null : newSortOrder,
          isActive: true,
        },
        // Refresh both variable sets in use: the management tab's unfiltered
        // list and the pickers' active-only list.
        refetchQueries: [
          { query: ALL_AGREEMENT_TYPES, variables: { page: 0, perPage: 1000, sortField: "sortOrder", sortOrder: "ASC" } },
          { query: ALL_AGREEMENT_TYPES, variables: { page: 0, perPage: 1000, sortField: "sortOrder", sortOrder: "ASC", filter: { status: "active" } } },
        ],
      });
      toast.push({ kind: "success", title: "Agreement type created", description: `${newName.trim()} is ready.` });
      resetCreateForm();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast.push({ kind: "error", title: "Couldn't create type", description: msg });
    }
  }

  async function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    if (!editingId || !editName.trim()) return;
    try {
      await updateAgreementType({
        variables: {
          id: editingId,
          name: editName.trim(),
          description: editDescription.trim() || null,
          sortOrder: editSortOrder === "" ? null : editSortOrder,
          isActive: editIsActive,
        },
        refetchQueries: [{ query: ALL_AGREEMENT_TYPES, variables: { page: 0, perPage: 1000, sortField: "sortOrder", sortOrder: "ASC" } }],
      });
      toast.push({ kind: "success", title: "Agreement type updated" });
      cancelEditing();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast.push({ kind: "error", title: "Couldn't update type", description: msg });
    }
  }

  async function handleDelete(t: AgreementTypeRecord) {
    if (usedTypeIds.has(t.id) || usedTypeKeys.has(t.typeKey)) {
      toast.push({
        kind: "error",
        title: "Cannot delete",
        description: `"${t.name}" is used by agreements or services. Retire it instead.`,
      });
      return;
    }
    const confirmed = window.confirm(`Delete "${t.name}"? This cannot be undone.`);
    if (!confirmed) return;
    try {
      await deleteAgreementType({
        variables: { id: t.id },
        refetchQueries: [{ query: ALL_AGREEMENT_TYPES, variables: { page: 0, perPage: 1000, sortField: "sortOrder", sortOrder: "ASC" } }],
      });
      toast.push({ kind: "success", title: "Agreement type deleted" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast.push({ kind: "error", title: "Couldn't delete type", description: msg });
    }
  }

  async function handleToggleActive(t: AgreementTypeRecord) {
    try {
      await updateAgreementType({
        variables: { id: t.id, isActive: !t.isActive },
        refetchQueries: [{ query: ALL_AGREEMENT_TYPES, variables: { page: 0, perPage: 1000, sortField: "sortOrder", sortOrder: "ASC" } }],
      });
      toast.push({
        kind: "success",
        title: t.isActive ? "Agreement type retired" : "Agreement type activated",
        description: `${t.name} is now ${t.isActive ? "retired" : "active"}.`,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast.push({ kind: "error", title: "Update failed", description: msg });
    }
  }

  if (agreementTypesLoading) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card p-6 space-y-3">
        <div className="h-3 bg-canvas-2 rounded w-48 pulse-soft" />
        <div className="h-3 bg-canvas-2 rounded w-64 pulse-soft" />
        <div className="h-20 bg-canvas-2 rounded pulse-soft" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-[13px] text-ink-3 max-w-xl">
          Agreement types are the document families your agreements belong to.
          Each service maps to one agreement type, and each agreement version is
          filed under one. Retire a type to hide it from pickers without removing it.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search
              size={13}
              strokeWidth={2}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-5 pointer-events-none"
            />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search types..."
              aria-label="Search agreement types"
              className="pl-8 pr-3 py-2 rounded-md hair border bg-paper-hi text-[12.5px] w-56 placeholder:text-ink-5 focus:outline-none focus:ring-2 focus:ring-tide/40"
            />
          </div>

          <select
            aria-label="Filter by status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ATStatusFilter)}
            className="px-2.5 py-2 rounded-md hair border bg-paper-hi text-[12px] text-ink-3"
          >
            <option value="all">All statuses</option>
            <option value="active">Active only</option>
            <option value="retired">Retired only</option>
          </select>
        </div>

        <Button
          variant="primary"
          icon={<Plus size={13} strokeWidth={2.2} />}
          onClick={() => setShowCreateForm(true)}
        >
          New agreement type
        </Button>
      </div>

      {/* Create form */}
      {showCreateForm && (
        <form onSubmit={handleCreate} className="bg-paper-hi rounded-lg shadow-card p-5 space-y-3">
          <div className="text-[13px] font-medium mb-2">New agreement type</div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                Name *
              </span>
              <input
                type="text"
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  if (!newTypeKeyEdited) setNewTypeKey(slugify(e.target.value));
                }}
                placeholder="Individual Fee Agreement"
                autoFocus
                className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
              />
            </label>
            <label className="block">
              <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                Type key *
              </span>
              <input
                type="text"
                value={newTypeKey}
                onChange={(e) => {
                  setNewTypeKey(e.target.value);
                  setNewTypeKeyEdited(true);
                }}
                placeholder="individual-fee-agreement"
                className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-tide/40"
              />
              <span className="text-[10.5px] text-ink-5 mt-0.5 block">
                Stable slug. Cannot be changed after creation.
              </span>
            </label>
          </div>
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <label className="block">
              <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                Description
              </span>
              <input
                type="text"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Optional description"
                className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
              />
            </label>
            <label className="block">
              <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                Sort order
              </span>
              <input
                type="number"
                value={newSortOrder}
                onChange={(e) =>
                  setNewSortOrder(e.target.value === "" ? "" : parseInt(e.target.value, 10) || 0)
                }
                min={0}
                className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] num focus:outline-none focus:ring-2 focus:ring-tide/40"
              />
            </label>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={resetCreateForm}
              className="px-3 py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!newName.trim() || creating}
              className="px-3.5 py-1.5 rounded-md bg-tide text-paper-hi text-[12.5px] font-medium hover:bg-tide-d disabled:bg-ink-6 disabled:cursor-not-allowed"
            >
              {creating ? "Creating..." : "Create"}
            </button>
          </div>
        </form>
      )}

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="bg-paper-hi rounded-lg shadow-card">
          <EmptyState
            icon={<FileText size={28} strokeWidth={1.5} />}
            heading={agreementTypes.length === 0 ? "No agreement types yet" : "No matches"}
            description={
              agreementTypes.length === 0
                ? "Create your first agreement type to organise your agreements into document families."
                : "No agreement types match the current filters."
            }
          />
        </div>
      ) : (
        <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-wash">
                <th className="text-[11px] font-semibold text-ink-3 uppercase tracking-wide px-4 py-2.5">
                  Name
                </th>
                <th className="text-[11px] font-semibold text-ink-3 uppercase tracking-wide px-4 py-2.5">
                  Type key
                </th>
                <th className="text-[11px] font-semibold text-ink-3 uppercase tracking-wide px-4 py-2.5">
                  Description
                </th>
                <th className="text-[11px] font-semibold text-ink-3 uppercase tracking-wide px-4 py-2.5 text-center">
                  Order
                </th>
                <th className="text-[11px] font-semibold text-ink-3 uppercase tracking-wide px-4 py-2.5 text-center">
                  Status
                </th>
                <th className="text-[11px] font-semibold text-ink-3 uppercase tracking-wide px-4 py-2.5 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-hair">
              {filtered.map((t) => {
                const isEditing = editingId === t.id;
                const isUsed = usedTypeIds.has(t.id) || usedTypeKeys.has(t.typeKey);

                if (isEditing) {
                  return (
                    <tr key={t.id} className="bg-tide-ss/30">
                      <td className="px-4 py-2.5" colSpan={6}>
                        <form onSubmit={handleUpdate} className="flex items-end gap-3 flex-wrap">
                          <label className="block flex-1 min-w-[160px]">
                            <span className="text-[10px] text-ink-4 uppercase tracking-wider font-semibold">Name</span>
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              autoFocus
                              className="w-full mt-0.5 px-2.5 py-1.5 rounded-md hair border bg-canvas/30 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-tide/40"
                            />
                          </label>
                          <label className="block flex-1 min-w-[160px]">
                            <span className="text-[10px] text-ink-4 uppercase tracking-wider font-semibold">Description</span>
                            <input
                              type="text"
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              className="w-full mt-0.5 px-2.5 py-1.5 rounded-md hair border bg-canvas/30 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-tide/40"
                            />
                          </label>
                          <label className="block w-20">
                            <span className="text-[10px] text-ink-4 uppercase tracking-wider font-semibold">Order</span>
                            <input
                              type="number"
                              value={editSortOrder}
                              onChange={(e) => setEditSortOrder(e.target.value === "" ? "" : parseInt(e.target.value, 10) || 0)}
                              min={0}
                              className="w-full mt-0.5 px-2.5 py-1.5 rounded-md hair border bg-canvas/30 text-[12.5px] num focus:outline-none focus:ring-2 focus:ring-tide/40"
                            />
                          </label>
                          <label className="flex items-center gap-1.5 pb-1">
                            <input
                              type="checkbox"
                              checked={editIsActive}
                              onChange={(e) => setEditIsActive(e.target.checked)}
                              className="w-3.5 h-3.5 rounded border-ink-5 text-tide focus:ring-tide/40"
                            />
                            <span className="text-[11.5px]">Active</span>
                          </label>
                          <div className="flex items-center gap-1.5 pb-0.5">
                            <button
                              type="button"
                              onClick={cancelEditing}
                              className="px-2.5 py-1.5 rounded-md hair border bg-paper-hi text-[11.5px] text-ink-3"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={!editName.trim() || updating}
                              className="px-2.5 py-1.5 rounded-md bg-tide text-paper-hi text-[11.5px] font-medium hover:bg-tide-d disabled:bg-ink-6 disabled:cursor-not-allowed"
                            >
                              {updating ? "Saving..." : "Save"}
                            </button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={t.id}
                    className={`${!t.isActive ? "opacity-60" : ""}`}
                  >
                    <td className="px-4 py-2.5">
                      <div className="text-[13px] font-medium">{t.name}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <code className="text-[12px] text-ink-4 font-mono">{t.typeKey}</code>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-[12.5px] text-ink-4">
                        {t.description || "--"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className="text-[12.5px] num text-ink-4">
                        {t.sortOrder ?? "--"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span
                        className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${
                          t.isActive ? "text-sage" : "text-ink-4"
                        }`}
                      >
                        <span className={`dot ${t.isActive ? "bg-sage" : "bg-ink-5"}`}></span>
                        {t.isActive ? "Active" : "Retired"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => startEditing(t)}
                          className="p-1.5 rounded text-ink-4 hover:text-ink-2 hover:bg-canvas/50 transition-colors"
                          title="Edit"
                        >
                          <Pencil size={13} strokeWidth={2} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggleActive(t)}
                          className="p-1.5 rounded text-ink-4 hover:text-ink-2 hover:bg-canvas/50 transition-colors"
                          title={t.isActive ? "Retire" : "Activate"}
                        >
                          {t.isActive ? (
                            <ToggleRight size={13} strokeWidth={2} />
                          ) : (
                            <ToggleLeft size={13} strokeWidth={2} />
                          )}
                        </button>
                        {!isUsed && (
                          <button
                            type="button"
                            onClick={() => handleDelete(t)}
                            disabled={deleting}
                            className="p-1.5 rounded text-ink-5 hover:text-rust hover:bg-rust/[0.06] transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={13} strokeWidth={2} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────────── */

export function AgreementsListing() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ToolbarFilters>(INITIAL_FILTERS);
  const [activeTab, setActiveTab] = useState<ActiveTab>("agreements");

  const agreementsQuery = useQuery<AllAgreementsData>(ALL_AGREEMENTS, {
    fetchPolicy: "cache-and-network",
  });
  const tenantQuery = useQuery<TenantDefaultsData>(TENANT_DEFAULTS, {
    fetchPolicy: "cache-and-network",
  });
  const btQuery = useQuery<AllBookingTypesData>(ALL_BOOKING_TYPES, {
    fetchPolicy: "cache-and-network",
  });
  const atQuery = useQuery<{ allAgreementTypes: AgreementTypeRecord[] }>(
    ALL_AGREEMENT_TYPES,
    {
      variables: { page: 0, perPage: 1000, sortField: "sortOrder", sortOrder: "ASC" },
      fetchPolicy: "cache-and-network",
    },
  );

  const agreements = agreementsQuery.data?.allAgreements ?? [];
  const services = btQuery.data?.allBookingTypes ?? [];
  const agreementTypes = atQuery.data?.allAgreementTypes ?? [];

  /** Build id -> name map from AgreementTypes for display. */
  const typeIdToName = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of agreementTypes) {
      map.set(t.id, t.name);
    }
    return map;
  }, [agreementTypes]);

  const generalStates = useMemo(
    () => STATE_OPTIONS.filter((o) => o.value).map((o) => o.value),
    [],
  );

  /** Gap count for the banner (null while data is still loading). */
  const gapCount = useMemo(() => {
    if (!agreementsQuery.data || !btQuery.data) return null;
    return computeCoverage(services, agreements, generalStates, typeIdToName).gapCount;
  }, [agreementsQuery.data, btQuery.data, services, agreements, generalStates, typeIdToName]);

  /** Type filter options: use AgreementType names when available. */
  const typeOptions = useMemo(() => {
    if (agreementTypes.length > 0) {
      return agreementTypes
        .filter((t) => t.isActive)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((t) => ({ value: t.id, label: t.name }));
    }
    // Fallback to raw string values if no types loaded
    const set = new Set<string>();
    for (const a of agreements) if (a.agreementType) set.add(a.agreementType);
    return Array.from(set).sort().map((t) => ({ value: t, label: t }));
  }, [agreements, agreementTypes]);

  const stateOptions = useMemo(() => {
    const set = new Set<string>();
    for (const a of agreements) if (a.state) set.add(a.state);
    return Array.from(set).sort();
  }, [agreements]);

  const filtered = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return agreements.filter((a) => {
      if (search && !a.name.toLowerCase().includes(search)) return false;
      if (filters.type) {
        // Filter by agreementTypeId if available, otherwise by agreementType string
        const matchesById = a.agreementTypeId === filters.type;
        const matchesByString = a.agreementType === filters.type;
        if (!matchesById && !matchesByString) return false;
      }
      if (filters.state && a.state !== filters.state) return false;
      if (filters.status === "active" && !a.isActive) return false;
      if (filters.status === "inactive" && a.isActive) return false;
      return true;
    });
  }, [agreements, filters]);

  /** Group filtered agreements by agreementTypeId (preferred) or agreementType string. */
  const documentGroups = useMemo(() => {
    const byType = new Map<string, Agreement[]>();
    for (const a of filtered) {
      // Key by agreementTypeId when present, fall back to agreementType string
      const key = a.agreementTypeId || a.agreementType || "";
      const list = byType.get(key);
      if (list) list.push(a);
      else byType.set(key, [a]);
    }
    return Array.from(byType.entries())
      .sort(([a], [b]) => {
        // Sort by AgreementType name if available
        const nameA = typeIdToName.get(a) ?? a;
        const nameB = typeIdToName.get(b) ?? b;
        return nameA.localeCompare(nameB);
      })
      .map(([key, versions]) => {
        // Use AgreementType name for the group heading
        const displayName =
          typeIdToName.get(key) ??
          versions[0]?.name ??
          key;
        const collisions = countJurisdictionCollisions(versions);
        return { key, displayName, versions, collisions };
      });
  }, [filtered, typeIdToName]);

  const pageHeader = (
    <div className="mb-5">
      <h2 className="font-display text-[26px] font-medium tracking-tight">
        Agreements
      </h2>
      <p className="text-[13px] text-ink-3 mt-1 max-w-xl">
        The contracts your customers sign. Defaults shape every agreement —
        per-agreement edits change body text and rare overrides.
      </p>
    </div>
  );

  if (agreementsQuery.error || tenantQuery.error) {
    const message =
      agreementsQuery.error?.message ?? tenantQuery.error?.message ?? "Unknown error";
    return (
      <div>
        {pageHeader}
        <div className="bg-paper-hi rounded-lg shadow-card p-8 text-center">
          <div className="font-medium text-[14px] mb-1">
            Couldn't load agreements
          </div>
          <div className="text-[12.5px] text-ink-4 mb-4">{message}</div>
          <Button
            variant="primary"
            onClick={() => {
              agreementsQuery.refetch();
              tenantQuery.refetch();
            }}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const initialLoad =
    (agreementsQuery.loading && !agreementsQuery.data) ||
    (tenantQuery.loading && !tenantQuery.data);

  return (
    <div>
      {pageHeader}

      {/* ── Tab bar ─────────────────────────────────────────────────── */}
      <div className="mb-6 inline-flex rounded-[10px] bg-paper-2 p-[3px]">
        <button
          type="button"
          onClick={() => setActiveTab("agreements")}
          className={`cursor-pointer rounded-[8px] px-5 py-[7px] text-[13px] font-semibold transition ${
            activeTab === "agreements"
              ? "bg-paper-hi text-ink shadow-sm"
              : "text-ink-4 hover:text-ink-3"
          }`}
        >
          Agreements
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("agreement-types")}
          className={`cursor-pointer rounded-[8px] px-5 py-[7px] text-[13px] font-semibold transition ${
            activeTab === "agreement-types"
              ? "bg-paper-hi text-ink shadow-sm"
              : "text-ink-4 hover:text-ink-3"
          }`}
        >
          Agreement Types
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("type-mappings")}
          className={`cursor-pointer rounded-[8px] px-5 py-[7px] text-[13px] font-semibold transition ${
            activeTab === "type-mappings"
              ? "bg-paper-hi text-ink shadow-sm"
              : "text-ink-4 hover:text-ink-3"
          }`}
        >
          Type mappings
        </button>
      </div>

      {/* ── Agreements tab ──────────────────────────────────────────── */}
      {activeTab === "agreements" && (
        <>
          {/* Coverage gap banner */}
          {gapCount !== null && gapCount > 0 && (
            <div className="mb-4 flex items-center gap-2 rounded-lg bg-rust/[0.06] px-4 py-2.5 text-[12.5px] text-rust">
              <AlertTriangle size={14} strokeWidth={2} className="flex-shrink-0" />
              <span>
                {gapCount} coverage gap{gapCount === 1 ? "" : "s"}: some services
                are offered where no agreement resolves.
              </span>
              <button
                type="button"
                onClick={() => setActiveTab("type-mappings")}
                className="ml-1 underline underline-offset-2 font-medium hover:text-rust/80 transition-colors"
              >
                View the coverage matrix
              </button>
            </div>
          )}

          <DefaultsStrip
            tenant={tenantQuery.data?.tenantSettings}
            loading={tenantQuery.loading && !tenantQuery.data}
          />

          <AgreementsToolbar
            filters={filters}
            onFiltersChange={setFilters}
            onNewAgreement={() => navigate("/settings/agreements/new")}
            typeOptions={typeOptions}
            stateOptions={stateOptions}
          />

          {initialLoad ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : agreements.length === 0 ? (
            <div className="bg-paper-hi rounded-lg shadow-card">
              <EmptyState
                icon={<FileText size={28} strokeWidth={1.5} />}
                heading="No agreements yet"
                description="Create your first agreement to get started."
              />
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-paper-hi rounded-lg shadow-card">
              <EmptyState
                icon={<FileText size={28} strokeWidth={1.5} />}
                heading="No matches"
                description="No agreements match the current filters."
              />
            </div>
          ) : (
            <div className="space-y-6">
              {documentGroups.map((group) => (
                <div key={group.key}>
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-[14px] font-medium">
                      {group.displayName}
                    </h3>
                    <span className="text-[11px] text-ink-4">
                      {group.versions.length}{" "}
                      {group.versions.length === 1 ? "version" : "versions"}
                    </span>
                    {group.versions.every((v) => !v.isActive) && group.versions.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10.5px] text-spark font-medium">
                        <AlertTriangle size={11} strokeWidth={2.5} />
                        No active version
                      </span>
                    )}
                    {group.collisions > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10.5px] text-spark font-medium">
                        <AlertTriangle size={11} strokeWidth={2.5} />
                        {group.collisions} jurisdiction{" "}
                        {group.collisions === 1 ? "collision" : "collisions"}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {group.versions.map((a) => (
                      <AgreementCard key={a.id} agreement={a} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Agreement Types tab ────────────────────────────────────── */}
      {activeTab === "agreement-types" && (
        <AgreementTypesTab
          agreementTypes={agreementTypes}
          agreementTypesLoading={atQuery.loading && !atQuery.data}
          agreements={agreements}
          services={services}
        />
      )}

      {/* ── Type mappings tab ───────────────────────────────────────── */}
      {activeTab === "type-mappings" && (
        <CoverageMatrix
          agreements={agreements}
          agreementsLoading={agreementsQuery.loading && !agreementsQuery.data}
        />
      )}
    </div>
  );
}
